/**
 * PaymentsTimeline — admin-only panel showing every Stripe payment_transaction
 * for a quote, with date / method / amount / status. Pulls from the same
 * balance-due-email-preview endpoint that powers the Send Balance Due dialog.
 *
 * Renders nothing if there are no payments.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { CheckCircle2, Clock, CreditCard, Banknote } from "lucide-react";
import { API } from "@/App";

const PaymentsTimeline = ({ quoteId }) => {
  const [payments, setPayments] = useState([]);
  const [meta, setMeta] = useState({ quote_total_base: 0, paid_base: 0, balance_due_base: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!quoteId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.post(`${API}/quotes/${quoteId}/balance-due-email-preview`, {
          origin_url: window.location.origin,
        });
        if (cancelled) return;
        setPayments(res.data?.payments || []);
        setMeta({
          quote_total_base: res.data?.quote_total_base || 0,
          paid_base: res.data?.paid_base || 0,
          balance_due_base: res.data?.balance_due_base || 0,
        });
      } catch {
        if (!cancelled) setPayments([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [quoteId]);

  if (loading || payments.length === 0) return null;

  const fmt = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
  const fully = meta.balance_due_base <= 0.01 && meta.paid_base > 0;

  return (
    <div
      className="bg-white border border-zinc-200 rounded-lg p-5 mb-6 shadow-sm"
      data-testid="payments-timeline"
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-zinc-800 uppercase tracking-wide flex items-center gap-2">
          <CreditCard className="w-4 h-4 text-[#00341d]" />
          Payment History
        </h3>
        <div className={`text-xs font-medium px-2.5 py-0.5 rounded-full ${
          fully ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"
        }`}>
          {fully ? "Fully Paid" : `Balance ${fmt(meta.balance_due_base)}`}
        </div>
      </div>

      <div className="space-y-2">
        {payments.map((p, i) => {
          const d = p.date ? new Date(p.date) : null;
          const dateStr = d && !Number.isNaN(d.getTime())
            ? d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })
            : "—";
          const isCard = p.method === "card";
          return (
            <div
              key={`pmt-${i}`}
              className="flex items-center gap-3 py-2 px-3 bg-zinc-50 rounded-md"
              data-testid={`timeline-payment-${i}`}
            >
              <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 text-sm font-medium text-zinc-800">
                  {isCard ? <CreditCard className="w-3.5 h-3.5 text-zinc-500" /> : <Banknote className="w-3.5 h-3.5 text-zinc-500" />}
                  <span>{isCard ? "Card" : (p.method || "Cash").replace("_", "/").replace(/\b\w/g, (c) => c.toUpperCase())} payment</span>
                </div>
                <div className="text-xs text-zinc-500 mt-0.5 flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {dateStr}
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-sm font-bold text-zinc-900 tabular-nums">{fmt(p.base_amount)}</div>
                {isCard && (
                  <div className="text-[10px] text-zinc-400">+ 3% fee charged</div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-4 pt-3 border-t border-zinc-200 flex items-center justify-between text-xs">
        <span className="text-zinc-500">Paid {fmt(meta.paid_base)} of {fmt(meta.quote_total_base)}</span>
        {!fully && (
          <span className="text-amber-700 font-medium">Outstanding: {fmt(meta.balance_due_base)}</span>
        )}
      </div>
    </div>
  );
};

export default PaymentsTimeline;
